/**
 * FocusShell.jsx — attention-support wrapper for in-activity screens.
 *
 * When the parent toggles Focus Mode ON, wrapping a page in
 * <FocusShell> renders a minimal top bar (Exit + progress + optional
 * pause) and hides the global Layout chrome via `body.focus-mode`.
 *
 * When Focus Mode is OFF (default), the shell is a passthrough — no
 * chrome hiding, no top bar — so existing behaviour is untouched.
 *
 * Props:
 *   • title       — short activity name in the top bar
 *   • progress    — { current, total } to render "3 of 7"
 *   • onExit      — required when active; called when the child taps
 *                   the Exit button. Save state before invoking.
 *   • onPause     — optional — pauses timers etc.
 *   • children    — the activity content.
 */
import React, { useEffect } from "react";
import { X, Pause } from "lucide-react";
import { useAttentionSettings } from "@/lib/attentionSettings";

/** Side progress panel — landscape tablets / desktop (lg+) use the spare width instead of empty margins. ONE component for every activity. */
export function ActivityProgressPanel({ title, current = 0, total = 0, steps = null, onExit, aside = null }) {
  const pct = total > 0 ? Math.round((Math.min(current, total) / total) * 100) : 0;
  return (
    <aside className="hidden lg:block lg:sticky lg:top-[68px] self-start" data-testid="activity-progress-panel" aria-label="Your progress">
      <div className="rounded-3xl border-[3px] border-slate-900 bg-white p-4 shadow-[0_4px_0_0_rgba(15,23,42,1)]">
        <p className="text-[11px] font-black uppercase tracking-widest text-slate-500">Your progress</p>
        {title && <p className="mt-1 font-heading text-base font-bold leading-snug text-slate-900 line-clamp-3">{title}</p>}
        {total > 0 && (
          <>
            <p className="mt-3 text-2xl font-black tabular-nums" data-testid="activity-progress-count">{Math.min(current, total)}<span className="text-sm text-slate-500 font-bold"> / {total}</span></p>
            <div className="mt-1 h-2.5 rounded-full bg-slate-200 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={Math.min(current, total)}>
              <div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-1 text-xs font-bold text-slate-600">{total - Math.min(current, total) === 0 ? "Last step!" : `${total - Math.min(current, total)} to go`}</p>
          </>
        )}
        {Array.isArray(steps) && steps.length > 0 && (
          <ol className="mt-3 space-y-1" data-testid="activity-progress-steps">
            {steps.slice(0, 12).map((label, i) => (
              <li key={i} className={`flex items-center gap-2 text-xs font-bold ${i + 1 < current ? "text-emerald-700" : i + 1 === current ? "text-slate-900" : "text-slate-400"}`}>
                <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 text-[10px] ${i + 1 < current ? "border-emerald-600 bg-emerald-100" : i + 1 === current ? "border-slate-900 bg-yellow-200" : "border-slate-300"}`}>{i + 1}</span>
                <span className="truncate">{label}</span>
              </li>
            ))}
          </ol>
        )}
        {aside}
        {onExit && (
          <button type="button" onClick={onExit} data-testid="activity-progress-exit" className="mt-4 w-full min-h-[44px] rounded-full border-2 border-slate-900 bg-white font-bold text-sm hover:bg-slate-50">
            Save &amp; exit
          </button>
        )}
      </div>
    </aside>
  );
}

export default function FocusShell({ title, progress, onExit, onPause, children, force = false, actions = null, steps = null, sidePanel = true }) {
  const { focusMode } = useAttentionSettings();
  const active = focusMode || force;

  useEffect(() => {
    if (!active) return undefined;
    document.body.classList.add("focus-mode");
    return () => document.body.classList.remove("focus-mode");
  }, [active]);

  if (!active) return <>{children}</>;

  const { current = 0, total = 0 } = progress || {};
  const pctLabel = total > 0 ? `${current} of ${total}` : null;

  return (
    <div data-testid="focus-shell" className="focus-shell">
      <header
        className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b-[3px] border-slate-900"
        data-testid="focus-shell-bar"
      >
        <div className="max-w-4xl mx-auto flex items-center gap-2 px-3 py-2">
          <button
            type="button"
            onClick={onExit}
            data-testid="focus-shell-exit"
            aria-label="Exit lesson"
            className="inline-flex items-center gap-1 h-11 px-3 rounded-full border-2 border-slate-900 bg-white font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform"
          >
            <X size={14} strokeWidth={3} /> Exit lesson
          </button>
          <p className="flex-1 text-center font-heading text-sm md:text-base font-bold text-slate-900 truncate"
             data-testid="focus-shell-title">
            {title || "Focus mode"}
          </p>
          {pctLabel ? (
            <span className="inline-flex items-center h-10 px-3 rounded-full border-2 border-slate-900 bg-yellow-100 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] tabular-nums"
                  data-testid="focus-shell-progress">
              {pctLabel}
            </span>
          ) : <span className="w-16" aria-hidden />}
          {onPause && (
            <button
              type="button"
              onClick={onPause}
              data-testid="focus-shell-pause"
              aria-label="Pause"
              className="inline-flex items-center justify-center h-10 w-10 rounded-full border-2 border-slate-900 bg-white shadow-[0_2px_0_0_rgba(15,23,42,1)]"
            >
              <Pause size={14} strokeWidth={3} />
            </button>
          )}
          {/* Lesson actions (Present / Print / Share / See-whole) surfaced
              in the focus bar so they stay reachable when focus mode hides
              the page's own action row. */}
          {actions}
        </div>
        {total > 0 && (
          <div className="h-1 w-full bg-slate-100" aria-hidden>
            <div
              className="h-full bg-emerald-400 transition-all duration-300"
              style={{ width: `${Math.round((current / total) * 100)}%` }}
            />
          </div>
        )}
      </header>
      {/* lg+ (landscape tablet / desktop): content + side progress panel; below lg the header bar is the only chrome. */}
      <main className={sidePanel ? "mx-auto max-w-4xl lg:max-w-6xl lg:grid lg:grid-cols-[minmax(0,1fr)_260px] lg:gap-6 lg:px-4" : "max-w-4xl mx-auto"}>
        <div className="min-w-0">{children}</div>
        {sidePanel && <ActivityProgressPanel title={title} current={current} total={total} steps={steps} onExit={onExit} />}
      </main>
    </div>
  );
}
